import { useMemo } from "react";
import { getColorFromCssVariable } from "ui";
import { LineChartCard } from "./shared/LineChartCard";
import { MetricDefinitions } from "./shared/MetricDefinitions";
import { useDatadogQuery } from "../DatadogAnalytics/useDatadogQuery";

type PerformanceData = {
  label: string;
  acceptance: number;
  success: number;
  capture: number;
}[];

export function PaymentPerformanceChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<PerformanceData>({
      chartType: "PAYMENT_PERFORMANCE",
    });

  const { labels, acceptance, success, capture } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0)
      return { labels: [], acceptance: [], success: [], capture: [] };

    return {
      labels: data.map((item) => item.label),
      acceptance: data.map((item) => item.acceptance),
      success: data.map((item) => item.success),
      capture: data.map((item) => item.capture),
    };
  }, [data]);

  const { acceptanceColor, successColor, captureColor } = useMemo(
    () => ({
      acceptanceColor: getColorFromCssVariable("--chart-categorical-1-subtle"),
      successColor: getColorFromCssVariable("--chart-categorical-2-subtle"),
      captureColor: getColorFromCssVariable("--chart-warning"),
    }),
    []
  );

  return (
    <LineChartCard
      ref={ref}
      title="Payment performance"
      description={
        <MetricDefinitions
          definitions={[
            {
              term: "Acceptance rate",
              description:
                "Share of intents that resulted in a successful transaction or an insufficient-funds decline.",
            },
            {
              term: "Success rate",
              description:
                "Share of intents that resulted in a successful transaction.",
            },
            {
              term: "Capture rate",
              description:
                "Share of intents that resulted in a captured payment.",
            },
          ]}
        />
      }
      format="percentage"
      labels={labels}
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
      datasets={[
        {
          label: "Acceptance",
          data: acceptance,
          backgroundColor: acceptanceColor,
          borderColor: acceptanceColor,
        },
        {
          label: "Success",
          data: success,
          backgroundColor: successColor,
          borderColor: successColor,
        },
        {
          label: "Capture",
          data: capture,
          backgroundColor: captureColor,
          borderColor: captureColor,
        },
      ]}
    />
  );
}
